import { CameraRig } from './cameraRig';
import { HudUI } from '../ui/hud';
import { AudioPlayer } from '../audio/player';
import { store } from '../app/store';
import { openShortcutsModal } from '../ui/shortcutsModal';
import { showToast } from '../ui/toast';
import { SCENE_DEFINITIONS } from '../config/scenes';

export class InputManager {
  private cameraRig: CameraRig;
  private hud: HudUI;
  private audioPlayer: AudioPlayer;
  private pressedKeys: Set<string> = new Set();
  private capturePhotoCallback?: () => void;

  constructor(cameraRig: CameraRig, hud: HudUI, audioPlayer: AudioPlayer, capturePhotoCallback?: () => void) {
    this.cameraRig = cameraRig;
    this.hud = hud;
    this.audioPlayer = audioPlayer;
    this.capturePhotoCallback = capturePhotoCallback;

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Ignore input if user is in an input or textarea
    if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
      return;
    }

    const key = e.key;
    this.pressedKeys.add(key.toLowerCase());

    // 1-6 Camera Views
    if (key >= '1' && key <= '6') {
      const viewId = parseInt(key, 10);
      const viewPill = document.querySelectorAll<HTMLButtonElement>('.view-pill')[viewId - 1];
      viewPill?.click();
      return;
    }

    switch (key) {
      case 't':
      case 'T':
        this.hud.cycleTimeOfDay(e.shiftKey ? -1 : 1);
        break;
      case ' ':
        e.preventDefault();
        {
          const isPaused = !store.getState().isPaused;
          store.setState({ isPaused });
          showToast(isPaused ? 'Tạm dừng cảnh' : 'Tiếp tục cảnh');
        }
        break;
      case 'x':
      case 'X':
        {
          const currentScale = store.getState().timeScale;
          const nextScale = currentScale === 0 ? 1 : 0;
          store.setState({ timeScale: nextScale });
          showToast(`Tốc độ thời gian: ${nextScale}x`);
        }
        break;
      case 'h':
      case 'H':
        this.hud.toggleHud();
        break;
      case 'p':
      case 'P':
        {
          const current = !store.getState().pixelArt;
          store.setState({ pixelArt: current });
          showToast(current ? 'Pixel Art: BẬT' : 'Pixel Art: TẮT');
        }
        break;
      case 'o':
      case 'O':
        {
          const current = !store.getState().inkLines;
          store.setState({ inkLines: current });
          showToast(current ? 'Nét mực thủy mặc: BẬT' : 'Nét mực: TẮT');
        }
        break;
      case 'f':
      case 'F':
        this.capturePhoto();
        break;
      case 'm':
      case 'M':
        {
          const isMuted = !store.getState().audioMuted;
          this.audioPlayer.setMute(isMuted);
          showToast(isMuted ? 'Tắt tiếng nhạc' : 'Bật tiếng nhạc');
        }
        break;
      case 'n':
      case 'N':
        this.audioPlayer.playNext();
        break;
      case 'b':
      case 'B':
        this.audioPlayer.playPrev();
        break;
      case '[':
        this.cycleScene(-1);
        break;
      case ']':
        this.cycleScene(1);
        break;
      case '?':
      case '/':
        openShortcutsModal();
        break;
    }

    this.updateFlyVector();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.pressedKeys.delete(e.key.toLowerCase());
    this.updateFlyVector();
  };

  private onBlur = (): void => {
    this.pressedKeys.clear();
    this.cameraRig.flyMoveVector.set(0, 0, 0);
    this.cameraRig.boatMoveVector.set(0, 0);
  };

  private updateFlyVector(): void {
    const keys = this.pressedKeys;
    let x = 0;
    let y = 0;
    let z = 0;

    if (keys.has('w')) z -= 1;
    if (keys.has('s')) z += 1;
    if (keys.has('a')) x -= 1;
    if (keys.has('d')) x += 1;
    if (keys.has('e')) y += 1;
    if (keys.has('q')) y -= 1;

    // Arrow keys for boat steering
    let boatTurn = 0;
    let boatFwd = 0;
    if (keys.has('arrowleft')) boatTurn -= 1;
    if (keys.has('arrowright')) boatTurn += 1;
    if (keys.has('arrowup')) boatFwd += 1;
    if (keys.has('arrowdown')) boatFwd -= 1;

    this.cameraRig.boatMoveVector.set(boatTurn, boatFwd);

    const isShift = keys.has('shift');
    const mult = isShift ? 2.5 : 1.0;
    this.cameraRig.flyMoveVector.set(x * mult, y * mult, z * mult);
  }

  private cycleScene(dir: number): void {
    const currentScene = store.getState().sceneId;
    const ids = SCENE_DEFINITIONS.map(s => s.id);
    const nextIdx = (ids.indexOf(currentScene) + dir + ids.length) % ids.length;
    window.location.hash = `#scene=${ids[nextIdx]}`;
  }

  private capturePhoto(): void {
    if (this.capturePhotoCallback) {
      this.capturePhotoCallback();
    }
  }

  public dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
  }
}
